/** 启用/禁用状态：ENABLED 启用 / DISABLED 禁用 */
export type EnableStatus = "ENABLED" | "DISABLED";

/** 启用状态枚举值 */
export const ENABLE_STATUS = {
  ENABLED: "ENABLED",
  DISABLED: "DISABLED",
} as const satisfies Record<string, EnableStatus>;

/** 可通过 Switch 切换的 inviteStatus 状态 */
const INVITE_TOGGLEABLE_STATUSES = new Set<string>([ENABLE_STATUS.ENABLED, ENABLE_STATUS.DISABLED]);

/** 启用状态 UI 展示配置 */
export const ENABLE_STATUS_CONFIG: Record<
  EnableStatus,
  { label: string; color: string; tagColor: string }
> = {
  ENABLED: { label: "启用", color: "#22c55e", tagColor: "success" },
  DISABLED: { label: "停用", color: "#9ca3af", tagColor: "default" },
};

/**
 * 判断是否为启用状态
 * @param status 启用/禁用状态
 */
export const isEnableStatusEnabled = (status?: EnableStatus | null): boolean =>
  status === ENABLE_STATUS.ENABLED;

/**
 * 判断 inviteStatus 是否支持启用/停用切换
 * @param status inviteStatus 字典 code
 */
export const isInviteStatusToggleable = (status?: string): status is EnableStatus =>
  !!status && INVITE_TOGGLEABLE_STATUSES.has(status);

/** inviteStatus 业务枚举兜底文案（字典未配置或编码不一致时） */
const INVITE_STATUS_FALLBACK_LABEL: Record<string, string> = {
  ENABLED: "启用",
  DISABLED: "停用",
  NONACTIVE: "未激活",
  EXPIRED: "已过期",
};

/**
 * inviteStatus Tag 颜色
 * @param status inviteStatus 字典 code
 */
export const getInviteStatusTagColor = (status?: string): string => {
  const key = status?.toUpperCase();
  if (key === ENABLE_STATUS.ENABLED) return "success";
  if (key === ENABLE_STATUS.DISABLED) return "default";
  if (key === "NONACTIVE") return "warning";
  if (key === "EXPIRED") return "error";
  return "default";
};

/**
 * 解析 inviteStatus 展示文案：字典优先，未命中时用业务兜底
 */
export const resolveInviteStatusLabel = (
  status: string,
  getDictLabel: (typeCode: string, value?: string | null) => string,
  dictCode = "inviteStatus",
): string => {
  const label = getDictLabel(dictCode, status);
  if (label && label !== status && label !== "—") return label;
  return INVITE_STATUS_FALLBACK_LABEL[status.toUpperCase()] ?? label ?? status;
};
